import React, { useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ChartBlock from "./ChartBlock";

/**
 * Shared pieces for the AI analyst chat — used by both the full page
 * (AiAnalyst.js) and the floating widget (AiChatWidget.js).
 */

const toolLabel = (name) =>
	name === "run_sql" ? "SQL query" : name === "run_mongo_aggregation" ? "Mongo query" : name;

export const ToolChip = React.memo(({ event }) => {
	const { name, input, result } = event;
	const statusIcon = !result ? "⏳" : result.ok ? "✓" : "✕";
	const detail = input?.query || (input?.pipeline ? JSON.stringify(input.pipeline, null, 2) : "");
	return (
		<details className={`ai-tool-chip ${result ? (result.ok ? "ok" : "failed") : "running"}`}>
			<summary>
				<span className="ai-tool-status">{statusIcon}</span>
				<span className="ai-tool-name">{toolLabel(name)}</span>
				<span className="ai-tool-purpose">{input?.purpose || ""}</span>
				{result?.ok && (
					<span className="ai-tool-meta">
						{result.rowCount} row{result.rowCount === 1 ? "" : "s"}
						{result.truncated ? " (truncated)" : ""} · {result.durationMs}ms
					</span>
				)}
				{result && !result.ok && <span className="ai-tool-meta error">{result.error}</span>}
			</summary>
			{detail && <pre className="ai-tool-detail">{detail}</pre>}
		</details>
	);
});

// Memoized: committed messages must not re-parse markdown (or re-render
// their charts) on every streaming tick of the NEXT answer.
export const MarkdownMessage = React.memo(({ content }) => (
	<ReactMarkdown
		remarkPlugins={[remarkGfm]}
		components={{
			// Charts must not render inside <pre> — unwrap them.
			pre({ children }) {
				const child = Array.isArray(children) ? children[0] : children;
				if (child?.props?.className === "language-chart") return children;
				return <pre>{children}</pre>;
			},
			code({ className, children, ...props }) {
				if (className === "language-chart") {
					return <ChartBlock source={String(children)} />;
				}
				return (
					<code className={className} {...props}>
						{children}
					</code>
				);
			},
		}}
	>
		{content}
	</ReactMarkdown>
));

/** The AI Analyst sparkle mark (launcher icon / header avatar). */
export const SparkleIcon = ({ size = 26 }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none">
		<path
			d="M12 2.5 L13.8 9.2 L20.5 11 L13.8 12.8 L12 19.5 L10.2 12.8 L3.5 11 L10.2 9.2 Z"
			fill="url(#ai-sparkle-grad)"
		/>
		<path
			d="M18.5 3 L19.2 5.4 L21.6 6.1 L19.2 6.8 L18.5 9.2 L17.8 6.8 L15.4 6.1 L17.8 5.4 Z"
			fill="#c4b5fd"
			opacity=".85"
		/>
		<defs>
			<linearGradient id="ai-sparkle-grad" x1="3" y1="3" x2="21" y2="20">
				<stop stopColor="#a5b4fc" />
				<stop offset="1" stopColor="#8b5cf6" />
			</linearGradient>
		</defs>
	</svg>
);

/**
 * Chat engine hook: ephemeral history + SSE streaming client.
 * Each mount gets an independent conversation.
 */
export const useAiChat = () => {
	const [messages, setMessages] = useState([]);
	const [streamingText, setStreamingText] = useState("");
	const [toolEvents, setToolEvents] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const abortRef = useRef(null);
	const streamingTextRef = useRef("");
	const toolEventsRef = useRef([]);

	const handleEvent = (event, data) => {
		if (event === "text_delta") {
			streamingTextRef.current += data.text;
			setStreamingText(streamingTextRef.current);
		} else if (event === "text_reset") {
			// Backend detected the streamed text was leaked tool-call syntax,
			// not an answer — discard it; the real answer streams next.
			streamingTextRef.current = "";
			setStreamingText("");
		} else if (event === "tool_start") {
			toolEventsRef.current = [...toolEventsRef.current, { ...data, result: null }];
			setToolEvents(toolEventsRef.current);
		} else if (event === "tool_result") {
			toolEventsRef.current = toolEventsRef.current.map((t) =>
				t.id === data.id ? { ...t, result: data } : t,
			);
			setToolEvents(toolEventsRef.current);
		} else if (event === "error") {
			setError(data.message);
		}
	};

	const send = async (question) => {
		const q = question.trim();
		if (!q || isLoading) return;

		const history = [
			...messages.map(({ role, content }) => ({ role, content })),
			{ role: "user", content: q },
		];
		setMessages((prev) => [...prev, { role: "user", content: q }]);
		setError(null);
		setIsLoading(true);
		streamingTextRef.current = "";
		toolEventsRef.current = [];
		setStreamingText("");
		setToolEvents([]);

		const abort = new AbortController();
		abortRef.current = abort;

		try {
			const res = await fetch(`${process.env.REACT_APP_BASE_URL}/ai-analyst/chat`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ messages: history }),
				signal: abort.signal,
			});

			if (!res.ok) {
				const body = await res.json().catch(() => ({}));
				throw new Error(body.error || `Request failed (${res.status})`);
			}

			const reader = res.body.getReader();
			const decoder = new TextDecoder();
			let buffer = "";
			for (;;) {
				const { done, value } = await reader.read();
				if (done) break;
				buffer += decoder.decode(value, { stream: true });
				let idx;
				while ((idx = buffer.indexOf("\n\n")) !== -1) {
					const frame = buffer.slice(0, idx);
					buffer = buffer.slice(idx + 2);
					if (!frame || frame.startsWith(":")) continue; // heartbeat
					const event = /^event: (.+)$/m.exec(frame)?.[1];
					const dataRaw = /^data: (.+)$/m.exec(frame)?.[1];
					if (!event || !dataRaw) continue;
					try {
						handleEvent(event, JSON.parse(dataRaw));
					} catch {
						/* skip malformed frame */
					}
				}
			}
		} catch (err) {
			if (err.name !== "AbortError") setError(err.message);
		} finally {
			// Commit whatever streamed into a permanent assistant message.
			// IMPORTANT: capture ref values NOW — the setMessages updater runs
			// later (at React flush time), after the refs below are cleared.
			const finalText = streamingTextRef.current;
			const finalToolEvents = toolEventsRef.current;
			if (finalText || finalToolEvents.length) {
				setMessages((prev) => [
					...prev,
					{ role: "assistant", content: finalText, toolEvents: finalToolEvents },
				]);
			}
			streamingTextRef.current = "";
			toolEventsRef.current = [];
			setStreamingText("");
			setToolEvents([]);
			setIsLoading(false);
			abortRef.current = null;
		}
	};

	const stop = () => abortRef.current?.abort();
	const clear = () => {
		setMessages([]);
		setError(null);
	};

	return { messages, streamingText, toolEvents, isLoading, error, send, stop, clear, abortRef };
};

/** Message list body shared by page and widget. */
export const ChatMessages = ({ chat, suggestions, onSuggestion, bottomRef, emptyState }) => {
	const { messages, streamingText, toolEvents, isLoading, error } = chat;
	return (
		<>
			{messages.length === 0 && !isLoading && (
				<div className="ai-empty-state">
					{emptyState}
					<div className="ai-suggestions">
						{suggestions.map((s) => (
							<button key={s} className="ai-suggestion" onClick={() => onSuggestion(s)}>
								{s}
							</button>
						))}
					</div>
				</div>
			)}

			{messages.map((m, i) =>
				m.role === "user" ? (
					<div key={i} className="ai-msg user">
						{m.content}
					</div>
				) : (
					<div key={i} className="ai-msg assistant">
						{m.toolEvents?.length > 0 && (
							<div className="ai-tool-chips">
								{m.toolEvents.map((t) => (
									<ToolChip key={t.id} event={t} />
								))}
							</div>
						)}
						<MarkdownMessage content={m.content} />
					</div>
				),
			)}

			{isLoading && (
				<div className="ai-msg assistant">
					{toolEvents.length > 0 && (
						<div className="ai-tool-chips">
							{toolEvents.map((t) => (
								<ToolChip key={t.id} event={t} />
							))}
						</div>
					)}
					{streamingText ? (
						<MarkdownMessage content={streamingText} />
					) : (
						<div className="ai-thinking">
							<span></span>
							<span></span>
							<span></span>
						</div>
					)}
				</div>
			)}

			{error && <div className="ai-error">⚠ {error}</div>}
			<div ref={bottomRef} />
		</>
	);
};
